import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppLayout from "../../../../shared/components/AppLayout";
import Button from "../../../../shared/components/Button";
import InfoTooltip from "../../../../shared/components/InfoTooltip";
import LoadingPage from "../../../../shared/components/LoadingPage";
import SearchInput from "../../../../shared/components/SearchInput";
import brandLogoUrl from "../../../../assets/images/tnp-logo-neon.png";
import Spinner from "../../../../shared/components/Spinner";
import Toast from "../../../../shared/components/Toast";
import { getSiteLanguage } from "../../../../shared/i18n/language";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { buildLoginHref } from "../../../../shared/lib/loginRedirect";
import { formatCpf } from "../../../../shared/utils/cpf";
import {
  formatEventDateOnlyRangeLabel,
  formatEventDateRangeLabel,
  formatTimeLabel,
} from "../../../../shared/utils/dateTime";
import { useAuth } from "../../../auth/shared/hooks/useAuth";
import usePublicOrganizerVisualIdentityTheme from "../../../auth/shared/hooks/usePublicOrganizerVisualIdentityTheme";
import { getChallengeByIdMaybeAuth } from "../../../challenges/services/challengeAdminService";
import { ChallengeDetail } from "../../../challenges/types/challengeAdmin";
import { sortRegistrationCategories } from "../../../challenges/utils/registrationCategoryOrder";
import {
  AvailablePartner,
  DiscountCouponPreview,
  EventChallengeLink,
  EventRegistration,
  MyEventRegistrationSummary,
  cancelMyEventRegistration,
  createEventRegistration,
  createPaymentIntentWithPayload,
  getEventChallengeLink,
  getMyEventRegistration,
  listAvailablePartners,
  listMyEventRegistrations,
  updateMyEventRegistrationShirtSizes,
  validateRegistrationDiscountCoupon,
} from "../../shared/services/eventRegistrationService";
import "./EventDetailsPage.css";

type EventRegistrationPageProps = {
  eventIndex?: number;
  eventId?: string;
};

type CheckoutStep = "data" | "redirecting" | "pending" | "confirmed";
type PaymentMethodChoice = "pix" | "credit_card" | "debit_card";
type CouponValidationStatus = "idle" | "valid" | "invalid";
type SidePreferenceChoice = "direita" | "esquerda" | "ambos";
type AvailabilityRestrictionPeriod = {
  label: string;
  startAt: string;
  endAt: string;
  type?: string;
};
const PARTNER_SEARCH_DEBOUNCE_MS = 1000;
const CATEGORY_ORDER = ["7a", "6a", "5a", "4a", "3a", "2a", "open"] as const;
const LOCALE_BY_LANGUAGE = {
  pt: "pt-BR",
  en: "en-US",
  es: "es-ES",
} as const;

function currentLocale() {
  return LOCALE_BY_LANGUAGE[getSiteLanguage()] || "pt-BR";
}

function initialsFromName(value: string) {
  return (
    String(value || "")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((chunk) => chunk[0]?.toUpperCase() || "")
      .join("") || "JG"
  );
}

function formatCurrency(cents: number | null | undefined) {
  if (!Number.isFinite(Number(cents)))
    return siteTexts.eventRegistration.fallback.toBeDefined;
  return new Intl.NumberFormat(currentLocale(), {
    style: "currency",
    currency: "BRL",
  }).format(Number(cents) / 100);
}

function categoryLabel(code?: string | null) {
  if (!code) return siteTexts.eventRegistration.fallback.toBeDefined;
  if (code === "7a") return siteTexts.common.category.c7a;
  if (code === "6a") return siteTexts.common.category.c6a;
  if (code === "5a") return siteTexts.common.category.c5a;
  if (code === "4a") return siteTexts.common.category.c4a;
  if (code === "3a") return siteTexts.common.category.c3a;
  if (code === "2a") return siteTexts.common.category.c2a;
  if (code === "open") return siteTexts.common.category.open;
  if (code === "free") return siteTexts.common.category.free;
  return String(code || "").toUpperCase();
}

function normalizeEventChallengeType(value?: string | null) {
  const text = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const compact = text.replace(/[^a-z0-9]/g, "");
  if (compact.includes("tournament") || compact.includes("torneio")) {
    return "tournament";
  }
  if (compact.includes("super8") || text.includes("super oito")) {
    return "super8";
  }
  if (compact.includes("poco")) return "poco";
  if (compact.includes("challenge") || compact.includes("desafio")) {
    return "challenge";
  }
  return compact;
}

function isPartnerSelectionAllowed(
  partner: AvailablePartner & { categoryEligible?: boolean },
) {
  return partner.available !== false && partner.categoryEligible !== false;
}

function getPartnerCategoryStatus(
  partner: AvailablePartner & { categoryEligible?: boolean },
) {
  const texts = siteTexts.eventRegistration.partnerCompatibility;
  const reason = String(partner.reason || "").trim();
  const categoryText =
    String(partner.categoryLabel || "").trim() ||
    (partner.category ? categoryLabel(partner.category) : "");

  if (isPartnerSelectionAllowed(partner)) {
    return categoryText
      ? `${categoryText} · ${texts.available}`
      : texts.available;
  }
  if (reason === "already_registered") return texts.unavailable;
  if (!String(partner.category || "").trim()) return texts.undefinedCategory;
  return categoryText
    ? `${categoryText} · ${texts.incompatible}`
    : texts.incompatible;
}

function challengeCategorySummary(input?: {
  categoryGender?: string | null;
  category?: string | null;
  categoryMale?: string | null;
  categoryFemale?: string | null;
}) {
  const gender = String(input?.categoryGender || "")
    .trim()
    .toLowerCase();
  if (gender === "mixed" || gender === "open") {
    const genderLabel =
      gender === "open"
        ? siteTexts.common.challengeGender.open
        : siteTexts.common.challengeGender.mixed;
    return `${genderLabel} · ${siteTexts.common.challengeGender.maleShort} ${categoryLabel(input?.categoryMale)} / ${siteTexts.common.challengeGender.femaleShort} ${categoryLabel(input?.categoryFemale)}`;
  }
  if (gender === "male") {
    return `${siteTexts.common.challengeGender.male} · ${categoryLabel(input?.category)}`;
  }
  if (gender === "female") {
    return `${siteTexts.common.challengeGender.female} · ${categoryLabel(input?.category)}`;
  }
  return categoryLabel(input?.category);
}

function resolveCheckoutUrl(input?: {
  invoiceUrl?: string | null;
  paymentLink?: string | null;
  paymentUrl?: string | null;
}) {
  return (
    String(input?.invoiceUrl || "").trim() ||
    String(input?.paymentLink || "").trim() ||
    String(input?.paymentUrl || "").trim() ||
    ""
  );
}

function paymentMethodFeeRate(method: PaymentMethodChoice) {
  if (method === "credit_card") return 0.07;
  if (method === "debit_card") return 0.03;
  return 0;
}

function formatPreferredSide(value?: string | null) {
  if (value === "direita") return siteTexts.challengeEngine.labels.right;
  if (value === "esquerda") return siteTexts.challengeEngine.labels.left;
  if (value === "ambos") {
    return siteTexts.eventRegistration.form.drawPairSide.bothSummary;
  }
  return siteTexts.challengeEngine.labels.sidePreferenceMissing;
}

function amountWithFeeCents(amountCents: number, method: PaymentMethodChoice) {
  const base = Math.max(0, Math.round(Number(amountCents || 0)));
  const rate = paymentMethodFeeRate(method);
  if (rate <= 0) return base;
  return Math.ceil(base * (1 + rate));
}

function availabilityPeriodKey(period?: AvailabilityRestrictionPeriod | null) {
  return [
    String(period?.label || "").trim(),
    String(period?.startAt || "").trim(),
    String(period?.endAt || "").trim(),
  ].join("|");
}

function normalizeCategoryCode(value: unknown): string {
  const raw = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]/g, "");
  if (!raw) return "";
  if (raw.includes("open") || raw.includes("aberta") || raw.includes("aberto")) {
    return "open";
  }
  if (raw.includes("free") || raw.includes("livre") || raw.includes("social")) {
    return "free";
  }
  if (raw.includes("iniciante")) return "7a";
  const explicit = raw.match(/(7a|6a|5a|4a|3a|2a)/);
  if (explicit?.[1]) return explicit[1];
  if (raw === "7" || raw.startsWith("7")) return "7a";
  if (raw === "6" || raw.startsWith("6")) return "6a";
  if (raw === "5" || raw.startsWith("5")) return "5a";
  if (raw === "4" || raw.startsWith("4")) return "4a";
  if (raw === "3" || raw.startsWith("3")) return "3a";
  if (raw === "2" || raw.startsWith("2")) return "2a";
  return "";
}

function normalizeUserSex(value: unknown): "male" | "female" | "" {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (raw === "male" || raw === "masculino" || raw === "m") return "male";
  if (raw === "female" || raw === "feminino" || raw === "f") return "female";
  return "";
}

function normalizeShirtSizes(value: unknown) {
  const input = Array.isArray(value) ? value : [];
  const unique = new Set<string>();
  const sizes: string[] = [];
  input.forEach((item) => {
    const size = String(item || "").trim();
    if (!size) return;
    const key = size.toLowerCase();
    if (unique.has(key)) return;
    unique.add(key);
    sizes.push(size);
  });
  return sizes;
}

function resolveChallengeGender(
  value: unknown,
): "male" | "female" | "mixed" | "open" | "" {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (raw === "male" || raw === "female" || raw === "mixed" || raw === "open")
    return raw;
  return "";
}

function resolveChallengeCategoryForUser({
  challengeGender,
  challengeCategory,
  challengeCategoryMale,
  challengeCategoryFemale,
  userSex,
}: {
  challengeGender: "male" | "female" | "mixed" | "open" | "";
  challengeCategory: string;
  challengeCategoryMale: string;
  challengeCategoryFemale: string;
  userSex: "male" | "female" | "";
}) {
  if (challengeGender === "male")
    return challengeCategoryMale || challengeCategory || "";
  if (challengeGender === "female")
    return challengeCategoryFemale || challengeCategory || "";
  if (challengeGender === "mixed" || challengeGender === "open") {
    if (userSex === "female") {
      return (
        challengeCategoryFemale ||
        challengeCategoryMale ||
        challengeCategory ||
        ""
      );
    }
    if (userSex === "male") {
      return (
        challengeCategoryMale ||
        challengeCategoryFemale ||
        challengeCategory ||
        ""
      );
    }
    return (
      challengeCategory ||
      challengeCategoryMale ||
      challengeCategoryFemale ||
      ""
    );
  }
  if (challengeCategory) return challengeCategory;
  if (userSex === "female")
    return challengeCategoryFemale || challengeCategoryMale || "";
  if (userSex === "male")
    return challengeCategoryMale || challengeCategoryFemale || "";
  return challengeCategoryMale || challengeCategoryFemale || "";
}

function isCategoryEligibleForChallenge({
  userCategory,
  challengeCategory,
}: {
  userCategory: string;
  challengeCategory: string;
}) {
  if (!challengeCategory) return true;
  if (challengeCategory === "free") return true;
  if (!userCategory) return false;
  const userIndex = CATEGORY_ORDER.indexOf(
    userCategory as (typeof CATEGORY_ORDER)[number],
  );
  const challengeIndex = CATEGORY_ORDER.indexOf(
    challengeCategory as (typeof CATEGORY_ORDER)[number],
  );
  if (userIndex < 0 || challengeIndex < 0) return false;
  const categoryDistance = challengeIndex - userIndex;
  return categoryDistance === 0 || categoryDistance === 1;
}

export default function EventRegistrationPage({
  eventIndex,
  eventId,
}: EventRegistrationPageProps) {
  const { user, token, loading: authLoading } = useAuth();
  const resolvedEventIndex = useMemo(() => {
    if (Number.isFinite(eventIndex) && Number(eventIndex) >= 0)
      return Number(eventIndex);
    return -1;
  }, [eventIndex]);
  const resolvedEventId = useMemo(() => {
    const byProp = String(eventId || "").trim();
    if (byProp) return byProp;
    if (resolvedEventIndex >= 0) return `evt-${resolvedEventIndex}`;
    return "";
  }, [eventId, resolvedEventIndex]);
  const registrationHref = useMemo(() => {
    if (resolvedEventId) {
      return `#/eventos/id/${encodeURIComponent(resolvedEventId)}/inscricao`;
    }
    if (resolvedEventIndex >= 0)
      return `#/eventos/${resolvedEventIndex}/inscricao`;
    return String(window.location.hash || "#/eventos");
  }, [resolvedEventId, resolvedEventIndex]);
  const loginHref = useMemo(
    () => buildLoginHref(registrationHref),
    [registrationHref],
  );

  useEffect(() => {
    if (authLoading || token) return;
    // Sem login não há como listar duplas/parceiros disponíveis nem
    // concluir a inscrição — manda para o login e volta para esta mesma
    // tela de inscrição depois.
    window.location.hash = loginHref;
  }, [authLoading, token, loginHref]);

  const [challengeLink, setChallengeLink] = useState<EventChallengeLink | null>(
    null,
  );
  const hasLink = Boolean(challengeLink);
  const registrationsOpen = Boolean(challengeLink?.registrationsOpen);
  const [challengeLinkLoading, setChallengeLinkLoading] = useState(
    Boolean(resolvedEventId),
  );
  const [myRegistration, setMyRegistration] =
    useState<EventRegistration | null>(null);
  const [myEventRegistrations, setMyEventRegistrations] = useState<
    MyEventRegistrationSummary[]
  >([]);
  const [myRegistrationLoading, setMyRegistrationLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    variant: "success" | "error";
  } | null>(null);
  const [teammateCpfDraft, setTeammateCpfDraft] = useState("");
  const [teammateNameDraft, setTeammateNameDraft] = useState("");
  const [partnerSearch, setPartnerSearch] = useState("");
  const [availablePartners, setAvailablePartners] = useState<
    AvailablePartner[]
  >([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [selectedRegistrationCategoryKey, setSelectedRegistrationCategoryKey] =
    useState("");
  const [playerShirtSize, setPlayerShirtSize] = useState("");
  const [partnerShirtSize, setPartnerShirtSize] = useState("");
  const [preferredSideChoice, setPreferredSideChoice] =
    useState<SidePreferenceChoice | "">("");
  const [
    selectedAvailabilityRestrictionPeriodKeys,
    setSelectedAvailabilityRestrictionPeriodKeys,
  ] = useState<string[]>([]);
  const [shirtSizeError, setShirtSizeError] = useState("");
  const [challengeDetail, setChallengeDetail] =
    useState<ChallengeDetail | null>(null);
  usePublicOrganizerVisualIdentityTheme(
    challengeDetail?.organizerUserId,
    challengeDetail?.isOfficial,
  );
  const payerName = user?.name || "";
  const payerEmail = user?.email || "";
  const payerPhone = user?.phone || "";
  const payerTaxId = user?.cpf;
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethodChoice>("pix");
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>("data");
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponPreview, setCouponPreview] =
    useState<DiscountCouponPreview | null>(null);
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponValidationStatus, setCouponValidationStatus] =
    useState<CouponValidationStatus>("idle");
  const couponApplyingRef = useRef(false);
  const registrationRefreshInFlight = useRef(false);
  const info = siteTexts.common.info;

  useEffect(() => {
    if (!resolvedEventId) return;
    let active = true;
    setChallengeLinkLoading(true);
    getEventChallengeLink(resolvedEventId)
      .then((data) => {
        if (!active) return;
        setChallengeLink(data);
      })
      .catch(() => {
        if (!active) return;
        setChallengeLink(null);
      })
      .finally(() => {
        if (active) setChallengeLinkLoading(false);
      });
    return () => {
      active = false;
    };
  }, [resolvedEventId]);

  useEffect(() => {
    const categories = Array.isArray(challengeLink?.registrationCategories)
      ? challengeLink.registrationCategories
      : [];
    if (categories.length === 0) {
      setSelectedRegistrationCategoryKey("");
      return;
    }
    const current = String(selectedRegistrationCategoryKey || "").trim();
    if (
      current &&
      categories.some((item) => String(item.categoryKey) === current)
    ) {
      return;
    }
    setSelectedRegistrationCategoryKey(
      String(categories[0]?.categoryKey || "").trim(),
    );
  }, [challengeLink?.registrationCategories, selectedRegistrationCategoryKey]);

  const refreshMyRegistration = useCallback(
    async ({ silent = true }: { silent?: boolean } = {}) => {
      if (!token || !resolvedEventId) {
        setMyRegistration(null);
        return;
      }
      const selectedCategoryKey = String(
        selectedRegistrationCategoryKey || "",
      ).trim();
      const categories = Array.isArray(challengeLink?.registrationCategories)
        ? challengeLink.registrationCategories
        : [];
      if (categories.length > 0 && !selectedCategoryKey) {
        setMyRegistration(null);
        return;
      }
      if (registrationRefreshInFlight.current) return;
      registrationRefreshInFlight.current = true;
      if (!silent) setMyRegistrationLoading(true);
      try {
        const data = await getMyEventRegistration(
          token,
          resolvedEventId,
          selectedCategoryKey,
        );
        setMyRegistration(data);
      } catch (_error) {
        setMyRegistration(null);
      } finally {
        registrationRefreshInFlight.current = false;
        if (!silent) setMyRegistrationLoading(false);
      }
    },
    [
      challengeLink?.registrationCategories,
      resolvedEventId,
      selectedRegistrationCategoryKey,
      token,
    ],
  );

  useEffect(() => {
    if (authLoading || !resolvedEventId) return;
    void refreshMyRegistration({ silent: false });
  }, [authLoading, resolvedEventId, refreshMyRegistration]);

  useEffect(() => {
    if (authLoading || !token || !resolvedEventId) {
      setMyEventRegistrations([]);
      return;
    }
    let active = true;
    listMyEventRegistrations(token)
      .then((rows) => {
        if (!active) return;
        setMyEventRegistrations(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!active) return;
        setMyEventRegistrations([]);
      });
    return () => {
      active = false;
    };
  }, [authLoading, resolvedEventId, token]);

  useEffect(() => {
    setTeammateCpfDraft(formatCpf(myRegistration?.teammateCpf || ""));
    setTeammateNameDraft(String(myRegistration?.teammateName || ""));
  }, [myRegistration?.teammateCpf, myRegistration?.teammateName]);

  const isTournamentRegistration = useMemo(
    () =>
      [challengeLink?.challengeType, challengeDetail?.challengeType].some(
        (value) => normalizeEventChallengeType(value) === "tournament",
      ),
    [challengeDetail?.challengeType, challengeLink?.challengeType],
  );

  const registrationCategories = useMemo(
    () =>
      sortRegistrationCategories(
        Array.isArray(challengeLink?.registrationCategories)
          ? challengeLink.registrationCategories
          : [],
      ),
    [challengeLink?.registrationCategories],
  );

  const registeredTournamentCategoryKeys = useMemo(() => {
    const keys = new Set<string>();
    myEventRegistrations.forEach((row) => {
      if (String(row.eventId || "").trim() !== resolvedEventId) return;
      if (row.status === "canceled") return;
      if (row.paymentStatus === "canceled" || row.paymentStatus === "refunded") {
        return;
      }
      const key = String(row.registrationCategoryKey || "")
        .trim()
        .toLowerCase();
      if (key) keys.add(key);
    });
    return keys;
  }, [myEventRegistrations, resolvedEventId]);

  const selectableRegistrationCategories = useMemo(
    () =>
      registrationCategories.filter((item) => {
        const key = String(item.categoryKey || "")
          .trim()
          .toLowerCase();
        return key && !registeredTournamentCategoryKeys.has(key);
      }),
    [registeredTournamentCategoryKeys, registrationCategories],
  );

  useEffect(() => {
    if (registrationCategories.length === 0) return;
    if (selectableRegistrationCategories.length === 0) {
      if (selectedRegistrationCategoryKey) {
        setSelectedRegistrationCategoryKey("");
      }
      return;
    }
    const current = String(selectedRegistrationCategoryKey || "").trim();
    if (
      current &&
      selectableRegistrationCategories.some(
        (item) => String(item.categoryKey || "").trim() === current,
      )
    ) {
      return;
    }
    setSelectedRegistrationCategoryKey(
      String(selectableRegistrationCategories[0]?.categoryKey || "").trim(),
    );
  }, [
    registrationCategories.length,
    selectableRegistrationCategories,
    selectedRegistrationCategoryKey,
  ]);

  const eligibleRegistrationCategoryKeys = useMemo(() => {
    if (!token || !user || registrationCategories.length === 0) return null;
    const userCategory = normalizeCategoryCode(user.category);
    const userSex = normalizeUserSex(user.sex);
    const allowed = new Set<string>();
    registrationCategories.forEach((item) => {
      const challengeGender = resolveChallengeGender(item.categoryGender);
      if (
        (challengeGender === "male" || challengeGender === "female") &&
        userSex !== challengeGender
      ) {
        return;
      }
      const requiredCategory = resolveChallengeCategoryForUser({
        challengeGender,
        challengeCategory: "",
        challengeCategoryMale: normalizeCategoryCode(item.categoryMale),
        challengeCategoryFemale: normalizeCategoryCode(item.categoryFemale),
        userSex,
      });
      if (
        isCategoryEligibleForChallenge({
          userCategory,
          challengeCategory: requiredCategory,
        })
      ) {
        const key = String(item.categoryKey || "").trim();
        if (key) allowed.add(key);
      }
    });
    return allowed;
  }, [registrationCategories, token, user]);

  useEffect(() => {
    if (!eligibleRegistrationCategoryKeys) return;
    if (eligibleRegistrationCategoryKeys.size === 0) return;
    const current = String(selectedRegistrationCategoryKey || "").trim();
    if (current && eligibleRegistrationCategoryKeys.has(current)) return;
    const firstEligible = selectableRegistrationCategories.find((item) =>
      eligibleRegistrationCategoryKeys.has(
        String(item.categoryKey || "").trim(),
      ),
    );
    if (firstEligible?.categoryKey) {
      setSelectedRegistrationCategoryKey(
        String(firstEligible.categoryKey).trim(),
      );
    }
  }, [
    eligibleRegistrationCategoryKeys,
    selectableRegistrationCategories,
    selectedRegistrationCategoryKey,
  ]);

  const selectedRegistrationCategory = useMemo(() => {
    const selectedKey = String(selectedRegistrationCategoryKey || "").trim();
    if (!selectedKey) return null;
    return (
      registrationCategories.find(
        (item) => String(item.categoryKey || "").trim() === selectedKey,
      ) || null
    );
  }, [registrationCategories, selectedRegistrationCategoryKey]);
  const selectedRegistrationCategoryRemainingSlots = Number(
    selectedRegistrationCategory?.remainingSlots ?? NaN,
  );
  const selectedCategoryWillJoinWaitlist =
    !myRegistration &&
    Boolean(selectedRegistrationCategory) &&
    Number.isFinite(selectedRegistrationCategoryRemainingSlots) &&
    selectedRegistrationCategoryRemainingSlots <= 0;
  const availabilityRestrictionPeriods = useMemo<AvailabilityRestrictionPeriod[]>(
    () =>
      isTournamentRegistration &&
      challengeLink?.availabilityRestrictionsEnabled &&
      Array.isArray(challengeLink.availabilityRestrictionPeriods)
        ? challengeLink.availabilityRestrictionPeriods
            .map((period) => ({
              label: String(period?.label || "").trim(),
              startAt: String(period?.startAt || "").trim(),
              endAt: String(period?.endAt || "").trim(),
              type: String(period?.type || "").trim(),
            }))
            .filter((period) => period.label && period.startAt && period.endAt)
        : [],
    [
      challengeLink?.availabilityRestrictionPeriods,
      challengeLink?.availabilityRestrictionsEnabled,
      isTournamentRegistration,
    ],
  );
  const selectedAvailabilityRestrictionPeriods = useMemo(() => {
    const selectedKeys = new Set(selectedAvailabilityRestrictionPeriodKeys);
    return availabilityRestrictionPeriods.filter((period) =>
      selectedKeys.has(availabilityPeriodKey(period)),
    );
  }, [
    availabilityRestrictionPeriods,
    selectedAvailabilityRestrictionPeriodKeys,
  ]);
  const canRequestAvailabilityRestriction =
    isTournamentRegistration &&
    Boolean(challengeLink?.availabilityRestrictionsEnabled) &&
    availabilityRestrictionPeriods.length > 0;
  const shouldShowAvailabilityRestrictionSection =
    isTournamentRegistration && Boolean(challengeLink?.availabilityRestrictionsEnabled);

  useEffect(() => {
    if (!canRequestAvailabilityRestriction) {
      if (selectedAvailabilityRestrictionPeriodKeys.length > 0) {
        setSelectedAvailabilityRestrictionPeriodKeys([]);
      }
      return;
    }
    const validKeys = new Set(availabilityRestrictionPeriods.map(availabilityPeriodKey));
    const nextKeys = selectedAvailabilityRestrictionPeriodKeys.filter((key) =>
      validKeys.has(key),
    );
    if (nextKeys.length !== selectedAvailabilityRestrictionPeriodKeys.length) {
      setSelectedAvailabilityRestrictionPeriodKeys(nextKeys);
    }
  }, [
    availabilityRestrictionPeriods,
    canRequestAvailabilityRestriction,
    selectedAvailabilityRestrictionPeriodKeys,
  ]);

  useEffect(() => {
    const selectedKey = String(selectedRegistrationCategoryKey || "")
      .trim()
      .toLowerCase();
    if (!selectedKey) {
      setMyRegistration(null);
      return;
    }
    const currentRegistrationCategoryKey = String(
      myRegistration?.registrationCategoryKey || "",
    )
      .trim()
      .toLowerCase();
    if (
      currentRegistrationCategoryKey &&
      currentRegistrationCategoryKey !== selectedKey
    ) {
      setMyRegistration(null);
    }
  }, [myRegistration?.registrationCategoryKey, selectedRegistrationCategoryKey]);

  const configuredShirtSizes = useMemo(
    () => {
      const globalSizes = normalizeShirtSizes(challengeLink?.shirtSizes);
      if (globalSizes.length > 0) return globalSizes;
      const detailSizes = normalizeShirtSizes(challengeDetail?.shirtSizes);
      if (detailSizes.length > 0) return detailSizes;
      return selectedRegistrationCategory?.shirtSizesEnabled
        ? normalizeShirtSizes(selectedRegistrationCategory?.shirtSizes)
        : (() => {
            return normalizeShirtSizes(
              registrationCategories.flatMap((category) =>
                category.shirtSizesEnabled ? category.shirtSizes || [] : [],
              ),
            );
          })();
    },
    [
      challengeLink?.shirtSizes,
      challengeDetail?.shirtSizes,
      registrationCategories,
      selectedRegistrationCategory?.shirtSizes,
      selectedRegistrationCategory?.shirtSizesEnabled,
    ],
  );
  const shirtSelectionAvailable =
    configuredShirtSizes.length > 0 &&
    challengeLink?.shirtSelectionAvailable !== false;
  const availableShirtSizes = useMemo(
    () => (shirtSelectionAvailable ? configuredShirtSizes : []),
    [configuredShirtSizes, shirtSelectionAvailable],
  );
  const shirtAvailabilityType =
    challengeLink?.shirtAvailabilityType === "period" ||
    challengeLink?.shirtAvailabilityType === "quantity"
      ? challengeLink.shirtAvailabilityType
      : "none";
  const shirtAvailableCount =
    typeof challengeLink?.shirtAvailableCount === "number"
      ? challengeLink.shirtAvailableCount
      : null;
  const shirtAvailabilityMessage = useMemo(() => {
    const texts = siteTexts.eventRegistration.shirts;
    if (configuredShirtSizes.length === 0) return "";
    if (challengeLink?.shirtUnavailableReason === "deadline") {
      return texts.deadlineClosed;
    }
    if (challengeLink?.shirtUnavailableReason === "sold_out") {
      return texts.soldOut;
    }
    if (shirtAvailabilityType === "quantity" && shirtAvailableCount !== null) {
      return shirtAvailableCount === 1
        ? texts.oneAvailable
        : texts.availableCount.replace("{count}", String(shirtAvailableCount));
    }
    return "";
  }, [
    challengeLink?.shirtUnavailableReason,
    configuredShirtSizes.length,
    shirtAvailabilityType,
    shirtAvailableCount,
  ]);

  const effectiveChallengeRuleForSelectedCategory = useMemo(() => {
    if (selectedRegistrationCategory) {
      return {
        categoryGender: selectedRegistrationCategory.categoryGender || "mixed",
        category: null,
        categoryMale: selectedRegistrationCategory.categoryMale || null,
        categoryFemale: selectedRegistrationCategory.categoryFemale || null,
      };
    }
    if (!challengeDetail) return null;
    return {
      categoryGender: challengeDetail.categoryGender || null,
      category: challengeDetail.category || null,
      categoryMale: challengeDetail.categoryMale || null,
      categoryFemale: challengeDetail.categoryFemale || null,
    };
  }, [challengeDetail, selectedRegistrationCategory]);

  const event = useMemo(() => {
    if (!challengeLink && !challengeDetail) return null;
    return {
      title:
        String(challengeLink?.challengeName || "").trim() ||
        String(
          challengeDetail?.challengeName || challengeDetail?.groupName || "",
        ).trim(),
      date: formatEventDateRangeLabel(
        challengeLink?.scheduledAt || challengeDetail?.scheduledAt || null,
        challengeLink?.scheduledEndAt || challengeDetail?.scheduledEndAt || null,
      ),
      address:
        String(challengeLink?.address || "").trim() ||
        String(challengeDetail?.address || "").trim(),
      registrationType:
        challengeLink?.registrationType || challengeDetail?.registrationType,
      amountPerPlayerCents: challengeLink?.amountPerPlayerCents ?? null,
    };
  }, [challengeDetail, challengeLink]);

  const isSuper8Challenge = useMemo(
    () =>
      [challengeLink?.challengeType, challengeDetail?.challengeType].some(
        (value) => normalizeEventChallengeType(value) === "super8",
      ),
    [challengeDetail?.challengeType, challengeLink?.challengeType],
  );

  const isFixedPairChallenge = useMemo(() => {
    if (isSuper8Challenge) return false;
    if (normalizeEventChallengeType(challengeDetail?.challengeType) === "poco") {
      return true;
    }
    const categoryRegistrationType = String(
      selectedRegistrationCategory?.registrationType || "",
    )
      .trim()
      .toLowerCase();
    if (categoryRegistrationType === "fixed_pair") return true;
    if (categoryRegistrationType === "individual") return false;
    if (challengeLink?.registrationType === "fixed_pair") return true;
    if (event?.registrationType === "fixed_pair") return true;
    return false;
  }, [
    challengeDetail?.challengeType,
    challengeLink?.registrationType,
    event?.registrationType,
    isSuper8Challenge,
    selectedRegistrationCategory?.registrationType,
  ]);

  const isDrawPairChallenge = useMemo(() => {
    if (isFixedPairChallenge || isSuper8Challenge) return false;
    return [challengeLink?.challengeType, challengeDetail?.challengeType].some(
      (value) => normalizeEventChallengeType(value) === "challenge",
    );
  }, [
    challengeDetail?.challengeType,
    challengeLink?.challengeType,
    isFixedPairChallenge,
    isSuper8Challenge,
  ]);

  useEffect(() => {
    const side = String(myRegistration?.preferredSide || "").trim();
    if (side === "direita" || side === "esquerda" || side === "ambos") {
      setPreferredSideChoice(side);
    }
  }, [myRegistration?.preferredSide]);

  useEffect(() => {
    if (!token || !hasLink || !isFixedPairChallenge || !resolvedEventId) {
      setAvailablePartners([]);
      setPartnersLoading(false);
      return;
    }
    const selectedCategoryKey = String(
      selectedRegistrationCategoryKey || "",
    ).trim();
    if (registrationCategories.length > 0 && !selectedCategoryKey) {
      setAvailablePartners([]);
      setPartnersLoading(false);
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      if (!active) return;
      setPartnersLoading(true);
      listAvailablePartners(
        token,
        resolvedEventId,
        partnerSearch,
        selectedCategoryKey,
      )
        .then((data) => {
          if (!active) return;
          setAvailablePartners(Array.isArray(data.items) ? data.items : []);
        })
        .catch(() => {
          if (!active) return;
          setAvailablePartners([]);
        })
        .finally(() => {
          if (active) setPartnersLoading(false);
        });
    }, PARTNER_SEARCH_DEBOUNCE_MS);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [
    token,
    hasLink,
    isFixedPairChallenge,
    resolvedEventId,
    partnerSearch,
    registrationCategories.length,
    selectedRegistrationCategoryKey,
  ]);

  useEffect(() => {
    setAvailablePartners([]);
    setPlayerShirtSize("");
    setPartnerShirtSize("");
    setShirtSizeError("");
    if (!myRegistration) {
      setTeammateCpfDraft("");
      setTeammateNameDraft("");
    }
  }, [myRegistration, selectedRegistrationCategoryKey]);

  useEffect(() => {
    setPlayerShirtSize(String(myRegistration?.shirtSizes?.player || ""));
    setPartnerShirtSize(String(myRegistration?.shirtSizes?.partner || ""));
    setShirtSizeError("");
  }, [myRegistration?.shirtSizes?.partner, myRegistration?.shirtSizes?.player]);

  useEffect(() => {
    if (availableShirtSizes.length === 0) {
      setPlayerShirtSize("");
      setPartnerShirtSize("");
      setShirtSizeError("");
      return;
    }
    setPlayerShirtSize((current) =>
      current && !availableShirtSizes.includes(current) ? "" : current,
    );
    setPartnerShirtSize((current) =>
      current && !availableShirtSizes.includes(current) ? "" : current,
    );
  }, [availableShirtSizes]);

  useEffect(() => {
    const tournamentId = Number(challengeLink?.tournamentId || 0);
    if (!Number.isFinite(tournamentId) || tournamentId <= 0) {
      setChallengeDetail(null);
      return;
    }
    let active = true;
    getChallengeByIdMaybeAuth(tournamentId, token || undefined)
      .then((data) => {
        if (!active) return;
        setChallengeDetail(data);
      })
      .catch(() => {
        if (!active) return;
        setChallengeDetail(null);
      });
    return () => {
      active = false;
    };
  }, [challengeLink?.tournamentId, token]);

  useEffect(() => {
    if (!myRegistration) return;
    if (couponApplyingRef.current) return;
    if (registrationCategories.length > 1) {
      setCheckoutStep("data");
      return;
    }
    if (myRegistration.paymentStatus === "confirmed") {
      setCheckoutStep("confirmed");
      return;
    }
    if (
      myRegistration.paymentRequired &&
      (myRegistration.paymentStatus === "pending" ||
        String(myRegistration.invoiceUrl || "").trim() ||
        String(myRegistration.paymentLink || "").trim())
    ) {
      setCheckoutStep((current) => (current === "data" ? "pending" : current));
    }
  }, [myRegistration, registrationCategories.length]);

  const shouldAutoRefreshRegistration = Boolean(
    token &&
    resolvedEventId &&
    myRegistration &&
    myRegistration?.status !== "canceled" &&
    myRegistration?.paymentStatus !== "confirmed",
  );

  useEffect(() => {
    if (!shouldAutoRefreshRegistration) return;
    const interval = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      void refreshMyRegistration({ silent: true });
    }, 5000);
    return () => window.clearInterval(interval);
  }, [refreshMyRegistration, shouldAutoRefreshRegistration]);

  useEffect(() => {
    if (!shouldAutoRefreshRegistration) return;
    const onFocus = () => {
      void refreshMyRegistration({ silent: true });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      void refreshMyRegistration({ silent: true });
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refreshMyRegistration, shouldAutoRefreshRegistration]);

  function writeCheckoutLoadingIntoPopup(popup: Window | null) {
    if (!popup || popup.closed) return;
    const title = siteTexts.eventRegistration.checkout.redirectingTitle;
    const text = siteTexts.eventRegistration.checkout.redirectingText;
    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    try {
      popup.document.open();
      popup.document.write(`<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  html, body { margin: 0; height: 100%; }
  body {
    background: #081320;
    color: #ffffff;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 40px 18px;
    box-sizing: border-box;
  }
  .checkout-loading { display: grid; justify-items: center; gap: 18px; max-width: 420px; text-align: center; }
  .checkout-loading__spinner { position: relative; width: 148px; height: 148px; display: grid; place-items: center; }
  .checkout-loading__ring {
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: conic-gradient(from 0deg, rgba(43, 156, 223, 0.95), rgba(229, 54, 130, 0.95), rgba(43, 156, 223, 0.95));
    animation: checkout-loading-spin 1.05s linear infinite;
    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 9px), #000 calc(100% - 8px));
    mask: radial-gradient(farthest-side, transparent calc(100% - 9px), #000 calc(100% - 8px));
  }
  .checkout-loading__logo { width: 92px; height: auto; z-index: 1; }
  .checkout-loading h1 { font-size: 18px; margin: 0; }
  .checkout-loading p { margin: 0; opacity: 0.85; font-size: 14px; line-height: 1.5; }
  @keyframes checkout-loading-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
</style>
</head>
<body>
  <div class="checkout-loading" role="status" aria-live="polite">
    <div class="checkout-loading__spinner">
      <div class="checkout-loading__ring"></div>
      <img class="checkout-loading__logo" src="${escapeHtml(brandLogoUrl)}" alt="TNP" />
    </div>
    <h1>${escapeHtml(title)}</h1>
    <p>${escapeHtml(text)}</p>
  </div>
</body>
</html>`);
      popup.document.close();
    } catch (_err) {
      // Se o navegador bloquear a escrita (ex.: about:blank restrito), o
      // popup segue em branco até o redirecionamento real acontecer.
    }
  }

  function openCheckoutInNewPage(url: string, popup?: Window | null) {
    if (!url) return false;
    let target = popup || null;
    if (target && !target.closed) {
      target.location.replace(url);
      return true;
    }
    target = window.open(url, "_blank");
    if (target && !target.closed) return true;
    window.location.href = url;
    return true;
  }

  const categoryMismatchMessage = useMemo(() => {
    if (
      !token ||
      !user ||
      !hasLink ||
      !effectiveChallengeRuleForSelectedCategory
    )
      return "";
    const userCategory = normalizeCategoryCode(user.category);
    if (!userCategory) {
      return siteTexts.eventRegistration.errors.profileCategoryRequired;
    }
    const challengeGender = resolveChallengeGender(
      effectiveChallengeRuleForSelectedCategory.categoryGender,
    );
    const userSex = normalizeUserSex(user.sex);
    if (
      (challengeGender === "male" || challengeGender === "female") &&
      userSex !== challengeGender
    ) {
      return siteTexts.eventRegistration.errors.categoryMismatch;
    }
    const challengeCategoryCode = resolveChallengeCategoryForUser({
      challengeGender,
      challengeCategory: normalizeCategoryCode(
        effectiveChallengeRuleForSelectedCategory.category,
      ),
      challengeCategoryMale: normalizeCategoryCode(
        effectiveChallengeRuleForSelectedCategory.categoryMale,
      ),
      challengeCategoryFemale: normalizeCategoryCode(
        effectiveChallengeRuleForSelectedCategory.categoryFemale,
      ),
      userSex,
    });
    if (
      !isCategoryEligibleForChallenge({
        userCategory,
        challengeCategory: challengeCategoryCode,
      })
    ) {
      return siteTexts.eventRegistration.errors.categoryMismatch;
    }
    return "";
  }, [effectiveChallengeRuleForSelectedCategory, hasLink, token, user]);

  const availablePartnersWithEligibility = useMemo(() => {
    const challengeGender = resolveChallengeGender(
      effectiveChallengeRuleForSelectedCategory?.categoryGender,
    );
    const currentUserSex = normalizeUserSex(user?.sex);
    return availablePartners.map((partner) => {
      const partnerSex = normalizeUserSex(partner.sex);
      if (
        (challengeGender === "male" || challengeGender === "female") &&
        partnerSex !== challengeGender
      ) {
        return { ...partner, categoryEligible: false };
      }
      if (
        isFixedPairChallenge &&
        challengeGender === "mixed" &&
        (!currentUserSex || !partnerSex || partnerSex === currentUserSex)
      ) {
        return { ...partner, categoryEligible: false };
      }
      const partnerCategory = normalizeCategoryCode(partner.category);
      const requiredCategory = resolveChallengeCategoryForUser({
        challengeGender,
        challengeCategory: normalizeCategoryCode(
          effectiveChallengeRuleForSelectedCategory?.category,
        ),
        challengeCategoryMale: normalizeCategoryCode(
          effectiveChallengeRuleForSelectedCategory?.categoryMale,
        ),
        challengeCategoryFemale: normalizeCategoryCode(
          effectiveChallengeRuleForSelectedCategory?.categoryFemale,
        ),
        userSex: partnerSex,
      });
      return {
        ...partner,
        categoryEligible: isCategoryEligibleForChallenge({
          userCategory: partnerCategory,
          challengeCategory: requiredCategory,
        }),
      };
    });
  }, [
    availablePartners,
    effectiveChallengeRuleForSelectedCategory,
    isFixedPairChallenge,
    user?.sex,
  ]);

  useEffect(() => {
    const selectedCpf = String(teammateCpfDraft || "").replace(/\D/g, "");
    if (!selectedCpf) return;
    const stillAllowed = availablePartnersWithEligibility.some(
      (partner) =>
        String(partner.cpf || "").trim() === selectedCpf &&
        isPartnerSelectionAllowed(partner),
    );
    if (stillAllowed) return;
    setTeammateCpfDraft("");
    setTeammateNameDraft("");
  }, [availablePartnersWithEligibility, teammateCpfDraft]);

  const getSelectedPartnerCompatibilityError = useCallback(() => {
    if (!isFixedPairChallenge) return "";
    const selectedCpf = String(teammateCpfDraft || "").replace(/\D/g, "");
    if (selectedCpf.length < 8) return "";
    const selectedPartner = availablePartnersWithEligibility.find(
      (partner) => String(partner.cpf || "").trim() === selectedCpf,
    );
    if (!selectedPartner || isPartnerSelectionAllowed(selectedPartner)) {
      return "";
    }
    if (selectedPartner.reason === "already_registered") {
      return siteTexts.eventRegistration.buttons.unavailablePartner;
    }
    return siteTexts.eventRegistration.partnerCompatibility.incompatiblePair;
  }, [
    availablePartnersWithEligibility,
    isFixedPairChallenge,
    teammateCpfDraft,
  ]);

  const getMissingShirtSizeMessage = useCallback(() => {
    if (availableShirtSizes.length === 0) return "";
    if (
      shirtAvailabilityType === "quantity" &&
      shirtAvailableCount !== null &&
      shirtAvailableCount <= 1
    ) {
      if (playerShirtSize && partnerShirtSize) {
        return siteTexts.eventRegistration.shirts.onlyOneAvailable;
      }
      return "";
    }
    if (!playerShirtSize) return "Selecione o tamanho da sua camiseta.";
    if (isFixedPairChallenge && !partnerShirtSize) {
      return "Selecione o tamanho da camiseta da sua dupla.";
    }
    return "";
  }, [
    availableShirtSizes.length,
    isFixedPairChallenge,
    partnerShirtSize,
    playerShirtSize,
    shirtAvailabilityType,
    shirtAvailableCount,
  ]);

  const ensureRegistrationForPayment = async (options?: {
    updateLocalState?: boolean;
  }) => {
    const updateLocalState = options?.updateLocalState !== false;
    let registration = myRegistration;
    const shirtSizesPayload = {
      player: availableShirtSizes.length > 0 ? playerShirtSize : "",
      partner:
        isFixedPairChallenge && availableShirtSizes.length > 0
          ? partnerShirtSize
          : "",
    };
    const availabilityRestrictionPayload =
      canRequestAvailabilityRestriction &&
      selectedAvailabilityRestrictionPeriods.length > 0
        ? {
            enabled: true,
            periodKeys: selectedAvailabilityRestrictionPeriods.map((period) =>
              availabilityPeriodKey(period),
            ),
            periods: selectedAvailabilityRestrictionPeriods.map((period) => ({
              periodKey: availabilityPeriodKey(period),
              label: period.label,
              startAt: period.startAt,
              endAt: period.endAt,
              type: period.type,
            })),
          }
        : undefined;
    const registrationPayload: NonNullable<
      Parameters<typeof createEventRegistration>[2]
    > = {
      participantMode: isFixedPairChallenge ? "dupla_fixa" : "individual",
      preferredSide: isDrawPairChallenge
        ? (preferredSideChoice as SidePreferenceChoice)
        : undefined,
      teammateCpf: String(teammateCpfDraft || "").replace(/\D/g, ""),
      teammateName: String(teammateNameDraft || "").trim(),
      shirtSizes: shirtSizesPayload,
      registrationCategoryKey: String(
        selectedRegistrationCategoryKey || "",
      ).trim(),
      availabilityRestriction: isTournamentRegistration
        ? availabilityRestrictionPayload || { enabled: false }
        : undefined,
    };
    if (!registration || Number(registration?.id || 0) <= 0) {
      const partnerCompatibilityError = getSelectedPartnerCompatibilityError();
      if (partnerCompatibilityError) {
        throw new Error(partnerCompatibilityError);
      }
      registration = await createEventRegistration(
        token || "",
        resolvedEventId,
        registrationPayload,
      );
      if (updateLocalState) {
        setMyRegistration(registration);
      }
    } else if (
      isTournamentRegistration &&
      registration.status !== "confirmed" &&
      registration.paymentStatus !== "confirmed"
    ) {
      registration = await createEventRegistration(
        token || "",
        resolvedEventId,
        registrationPayload,
      );
      if (updateLocalState) {
        setMyRegistration(registration);
      }
    } else if (availableShirtSizes.length > 0) {
      const currentPlayerShirtSize = String(
        registration.shirtSizes?.player || "",
      ).trim();
      const currentPartnerShirtSize = String(
        registration.shirtSizes?.partner || "",
      ).trim();
      if (
        currentPlayerShirtSize !== shirtSizesPayload.player ||
        currentPartnerShirtSize !== shirtSizesPayload.partner
      ) {
        registration = await updateMyEventRegistrationShirtSizes(
          token || "",
          registration.id,
          { shirtSizes: shirtSizesPayload },
        );
        if (updateLocalState) {
          setMyRegistration(registration);
        }
      }
    }
    return registration;
  };

  const onApplyCoupon = async () => {
    const safeCouponCode = String(couponCode || "").trim();
    if (!safeCouponCode) {
      setCouponPreview(null);
      setCouponValidationStatus("idle");
      setToast({
        message: siteTexts.eventRegistration.discountCoupon.empty,
        variant: "error",
      });
      return;
    }
    if (!token) {
      window.location.hash = loginHref;
      return;
    }
    const missingShirtSizeMessage = getMissingShirtSizeMessage();
    if (missingShirtSizeMessage) {
      setCouponValidationStatus("idle");
      setShirtSizeError(missingShirtSizeMessage);
      setToast({ message: missingShirtSizeMessage, variant: "error" });
      return;
    }
    couponApplyingRef.current = true;
    setCouponApplying(true);
    setToast(null);
    try {
      const registration = await ensureRegistrationForPayment({
        updateLocalState: false,
      });
      const preview = await validateRegistrationDiscountCoupon(
        token,
        registration.id,
        {
          couponCode: safeCouponCode,
          paymentMethod: selectedPaymentMethod,
        },
      );
      setCouponPreview(preview);
      setCouponValidationStatus("valid");
      setToast({
        message: siteTexts.eventRegistration.discountCoupon.applied,
        variant: "success",
      });
      setCheckoutStep("data");
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : siteTexts.eventRegistration.discountCoupon.invalid;
      setCouponPreview(null);
      const isShirtSizeError = errorMessage.toLowerCase().includes("camiseta");
      setCouponValidationStatus("invalid");
      if (isShirtSizeError) {
        setCouponValidationStatus("idle");
        setShirtSizeError(errorMessage);
      }
      setToast({
        message: errorMessage,
        variant: "error",
      });
    } finally {
      couponApplyingRef.current = false;
      setCouponApplying(false);
      setCheckoutStep("data");
    }
  };

  const clearSelectedPair = useCallback(async () => {
    const registrationId = Number(myRegistration?.id || 0);
    const canCancelPersistedRegistration =
      Boolean(token) &&
      Number.isFinite(registrationId) &&
      registrationId > 0 &&
      myRegistration?.paymentStatus !== "confirmed" &&
      myRegistration?.status !== "confirmed";

    setToast(null);
    try {
      if (canCancelPersistedRegistration) {
        setMyRegistrationLoading(true);
        await cancelMyEventRegistration(token || "", registrationId);
      }
      setMyRegistration(null);
      if (registrationId > 0) {
        setMyEventRegistrations((current) =>
          current.filter((row) => Number(row.id) !== registrationId),
        );
      }
      setCheckoutUrl("");
      setCouponCode("");
      setCouponPreview(null);
      setCouponValidationStatus("idle");
      setTeammateCpfDraft("");
      setTeammateNameDraft("");
      setPartnerShirtSize("");
      setShirtSizeError("");
      setCheckoutStep("data");
    } catch (err) {
      setToast({
        message:
          err instanceof Error
            ? err.message
            : siteTexts.eventRegistration.removeSelectedPairError,
        variant: "error",
      });
    } finally {
      setMyRegistrationLoading(false);
    }
  }, [myRegistration, token]);

  useEffect(() => {
    setCouponPreview(null);
    setCouponValidationStatus("idle");
  }, [couponCode, selectedPaymentMethod, selectedRegistrationCategoryKey]);

  const onSubmitAndPay = async () => {
    setToast(null);
    if (!token) {
      window.location.hash = loginHref;
      return;
    }
    if (!registrationsOpen && !myRegistration) {
      setToast({
        message: siteTexts.eventRegistration.registrationClosed,
        variant: "error",
      });
      return;
    }
    if (categoryMismatchMessage) {
      setToast({
        message: categoryMismatchMessage,
        variant: "error",
      });
      return;
    }
    if (isFixedPairChallenge) {
      const teammateCpfDigits = String(teammateCpfDraft || "").replace(
        /\D/g,
        "",
      );
      if (teammateCpfDigits.length < 8) {
        setToast({
          message: siteTexts.eventRegistration.errors.teammateCpfRequired,
          variant: "error",
        });
        return;
      }
      const partnerCompatibilityError = getSelectedPartnerCompatibilityError();
      if (partnerCompatibilityError) {
        setToast({
          message: partnerCompatibilityError,
          variant: "error",
        });
        return;
      }
    }
    if (isDrawPairChallenge && !preferredSideChoice) {
      setToast({
        message: siteTexts.eventRegistration.errors.preferredSideRequired,
        variant: "error",
      });
      return;
    }
    if (availableShirtSizes.length > 0) {
      const missingShirtSizeMessage = getMissingShirtSizeMessage();
      if (missingShirtSizeMessage) {
        setShirtSizeError(missingShirtSizeMessage);
        setToast({ message: missingShirtSizeMessage, variant: "error" });
        return;
      }
    }
    if (registrationCategories.length > 0) {
      const selectedCategoryKey = String(
        selectedRegistrationCategoryKey || "",
      ).trim();
      if (!selectedCategoryKey) {
        setToast({
          message:
            siteTexts.eventRegistration.errors.registrationCategoryRequired,
          variant: "error",
        });
        return;
      }
    }
    if (
      selectedCategoryWillJoinWaitlist &&
      !window.confirm(siteTexts.eventRegistration.status.waitlistConfirmation)
    ) {
      return;
    }
    const payerTaxIdDigits = String(payerTaxId || "").replace(/\D/g, "");
    const payerPhoneDigits = String(payerPhone || "").replace(/\D/g, "");
    if (!String(payerName || "").trim()) {
      setToast({
        message: siteTexts.eventRegistration.errors.fullNameRequired,
        variant: "error",
      });
      return;
    }
    if (!String(payerEmail || "").trim()) {
      setToast({
        message: siteTexts.eventRegistration.errors.emailRequired,
        variant: "error",
      });
      return;
    }
    if (!payerPhoneDigits || payerPhoneDigits.length < 10) {
      setToast({
        message: siteTexts.eventRegistration.errors.phoneInvalid,
        variant: "error",
      });
      return;
    }
    if (!(payerTaxIdDigits.length === 11 || payerTaxIdDigits.length === 14)) {
      setToast({
        message: siteTexts.eventRegistration.errors.taxIdInvalid,
        variant: "error",
      });
      return;
    }
    let checkoutPopup: Window | null = null;
    try {
      checkoutPopup = window.open("about:blank", "_blank");
    } catch (_err) {
      checkoutPopup = null;
    }
    writeCheckoutLoadingIntoPopup(checkoutPopup);
    setCheckoutStep("redirecting");
    try {
      setMyRegistrationLoading(true);
      const registration = await ensureRegistrationForPayment();
      const result = await createPaymentIntentWithPayload(
        token,
        registration.id,
        {
          fullName: String(payerName || "").trim(),
          email: String(payerEmail || "").trim(),
          phone: String(payerPhone || "").trim(),
          taxId: payerTaxIdDigits,
          paymentMethod: selectedPaymentMethod,
          couponCode: String(couponCode || "").trim(),
        },
      );
      if (result.ok) {
        setCouponPreview({
          ok: true,
          couponCode: result.couponCode,
          originalAmountCents: result.originalAmountCents,
          discountAmountCents: result.discountAmountCents,
          baseAmountCents: result.baseAmountCents,
          surchargeCents: result.surchargeCents,
          finalAmountCents: result.finalAmountCents ?? result.amountCents,
          selectedPaymentMethod: result.selectedPaymentMethod,
        });
      }
      if (!result.ok && result.error) {
        checkoutPopup?.close();
        setToast({ message: result.error, variant: "error" });
        setCheckoutStep("data");
        return;
      }
      const checkoutUrl = resolveCheckoutUrl(result);
      if (checkoutUrl) {
        const opened = openCheckoutInNewPage(checkoutUrl, checkoutPopup);
        setCheckoutUrl(checkoutUrl);
        if (!opened) {
          setToast({
            message: siteTexts.eventRegistration.toasts.openPopupError,
            variant: "error",
          });
          setCheckoutStep("pending");
          return;
        }
        setToast({
          message: siteTexts.eventRegistration.toasts.openPaymentSuccess,
          variant: "success",
        });
        window.setTimeout(() => setCheckoutStep("pending"), 1100);
        return;
      }
      checkoutPopup?.close();
      setToast({
        message: siteTexts.eventRegistration.toasts.checkoutStartError,
        variant: "error",
      });
      setCheckoutStep("data");
    } catch (err) {
      checkoutPopup?.close();
      setToast({
        message: err instanceof Error ? err.message : String(err),
        variant: "error",
      });
      setCheckoutStep("data");
    } finally {
      setMyRegistrationLoading(false);
    }
  };

  if (challengeLinkLoading) {
    return <LoadingPage label={siteTexts.eventDetails.loading} />;
  }

  if (!event) {
    return (
      <AppLayout title={siteTexts.eventDetails.notFoundTitle}>
        <div className="empty-state">
          <p>{siteTexts.eventDetails.notFoundMessage}</p>
          <Button variant="primary" href="#/eventos">
            {siteTexts.eventRegistration.buttons.backToEvents}
          </Button>
        </div>
      </AppLayout>
    );
  }

  const amountPerPlayerCents = Number.isFinite(
    Number(challengeLink?.amountPerPlayerCents),
  )
    ? Number(challengeLink?.amountPerPlayerCents)
    : Number.isFinite(Number(event?.amountPerPlayerCents))
      ? Number(event?.amountPerPlayerCents)
      : 0;
  const baseTotalAmountCents = isFixedPairChallenge
    ? amountPerPlayerCents * 2
    : amountPerPlayerCents;
  const availabilityRestrictionUnitAmountCents = Math.max(
    0,
    Math.round(Number(challengeLink?.availabilityRestrictionAmountCents || 0)),
  );
  const availabilityRestrictionAmountCents = canRequestAvailabilityRestriction
    ? availabilityRestrictionUnitAmountCents *
      selectedAvailabilityRestrictionPeriods.length *
      (isFixedPairChallenge ? 2 : 1)
    : 0;
  const payableBaseTotalAmountCents =
    baseTotalAmountCents + availabilityRestrictionAmountCents;
  const totalAmountCents = amountWithFeeCents(
    payableBaseTotalAmountCents,
    selectedPaymentMethod,
  );
  const discountAmountCents = Math.max(
    0,
    Math.round(Number(couponPreview?.discountAmountCents || 0)),
  );
  const discountedBaseAmountCents = Math.max(
    0,
    Math.round(
      Number(
          couponPreview?.baseAmountCents ??
          payableBaseTotalAmountCents - discountAmountCents,
      ),
    ),
  );
  const effectiveTotalAmountCents =
    couponPreview?.finalAmountCents !== undefined
      ? Math.max(0, Math.round(Number(couponPreview.finalAmountCents || 0)))
      : totalAmountCents;
  const paymentSurchargeCents = Math.max(
    0,
    effectiveTotalAmountCents - discountedBaseAmountCents,
  );
  const amountPerPlayerLabel = formatCurrency(amountPerPlayerCents);
  const baseTotalAmountLabel = formatCurrency(baseTotalAmountCents);
  const discountAmountLabel = formatCurrency(discountAmountCents);
  const paymentSurchargeLabel = formatCurrency(paymentSurchargeCents);
  const totalAmountLabel = formatCurrency(effectiveTotalAmountCents);
  const challengeCategory = challengeCategorySummary(
    challengeDetail || undefined,
  );
  const summaryLocation =
    String(selectedRegistrationCategory?.address || "").trim() ||
    String(challengeLink?.address || "").trim() ||
    String(challengeDetail?.address || "").trim() ||
    String(event?.address || "").trim() ||
    siteTexts.eventRegistration.fallback.toBeDefined;
  const summaryDateLabel =
    formatEventDateRangeLabel(
      selectedRegistrationCategory?.scheduledAt ||
        challengeLink?.scheduledAt ||
        challengeDetail?.scheduledAt ||
        null,
      selectedRegistrationCategory?.scheduledEndAt ||
        challengeLink?.scheduledEndAt ||
        challengeDetail?.scheduledEndAt ||
        null,
    ) ||
    String(event?.date || "").trim() ||
    siteTexts.eventRegistration.fallback.toBeDefined;
  const challengeCategoryLabel =
    String(selectedRegistrationCategory?.categoryName || "").trim() ||
    challengeCategory;
  const registrationPageTitle = [
    siteTexts.eventRegistration.steps.registration,
    String(event.title || "").trim(),
  ]
    .filter(Boolean)
    .join(" ");
  const fullName =
    String(user?.name || "").trim() ||
    siteTexts.eventRegistration.fallback.player;
  const teammateCpfDigits = String(teammateCpfDraft || "").replace(/\D/g, "");
  const hasSelectedFixedPairPartner =
    !isFixedPairChallenge || teammateCpfDigits.length >= 8;
  const shouldShowShirtSizeSelection =
    availableShirtSizes.length > 0 && hasSelectedFixedPairPartner;
  const shouldShowShirtSizeInPairCard =
    isFixedPairChallenge && shouldShowShirtSizeSelection;
  const playerShirtSizeLabel = String(payerName || fullName || "").trim();
  const partnerShirtSizeLabel =
    String(teammateNameDraft || "").trim() ||
    (isFixedPairChallenge ? "Parceiro da dupla" : "");
  const selectedAvailabilityRestrictionLabels =
    selectedAvailabilityRestrictionPeriods
      .map((period) => period.label)
      .filter(Boolean)
      .join(" • ");
  const availabilityRestrictionSelection =
    shouldShowAvailabilityRestrictionSection ? (
      <div className="event-register-v2-availability-card">
        <div className="event-register-v2-panel-title-row">
          <h3 className="info-label">
            Restrição de disponibilidade
            <InfoTooltip
              content={
                siteTexts.eventRegistration.form.availabilityRestrictionTooltip
              }
            />
          </h3>
        </div>
        <p>
          Marque os períodos em que você não pode jogar. O adicional é somado ao
          valor da inscrição.
        </p>
        {availabilityRestrictionPeriods.length > 0 ? (
          <div
            className="event-register-v2-availability-options"
            role="group"
            aria-label={
              siteTexts.eventRegistration.form
                .availabilityRestrictionPeriodsLabel
            }
          >
            {availabilityRestrictionPeriods.map((period) => {
              const key = availabilityPeriodKey(period);
              const checked =
                selectedAvailabilityRestrictionPeriodKeys.includes(key);
              return (
                <label
                  key={key}
                  className={`event-register-v2-availability-option${
                    checked ? " is-selected" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(inputEvent) => {
                      const { checked: nextChecked } = inputEvent.target;
                      setSelectedAvailabilityRestrictionPeriodKeys((current) =>
                        nextChecked
                          ? current.includes(key)
                            ? current
                            : [...current, key]
                          : current.filter((item) => item !== key),
                      );
                      setCouponPreview(null);
                      setCouponValidationStatus("idle");
                    }}
                    disabled={myRegistrationLoading}
                  />
                  <span>
                    <strong>{period.label}</strong>
                    <small>
                      {formatEventDateOnlyRangeLabel(
                        period.startAt,
                        period.endAt,
                      )}
                    </small>
                  </span>
                </label>
              );
            })}
          </div>
        ) : (
          <small>
            Nenhum período de restrição foi configurado para este torneio.
          </small>
        )}
        {selectedAvailabilityRestrictionPeriods.length > 0 &&
          availabilityRestrictionAmountCents > 0 && (
            <small>
              Adicional: {formatCurrency(availabilityRestrictionAmountCents)}
            </small>
          )}
      </div>
    ) : null;
  const selectedPartnerCompatibilityError =
    getSelectedPartnerCompatibilityError();
  const canSubmitAndPay =
    Boolean(token) &&
	    Boolean(hasLink) &&
	    !categoryMismatchMessage &&
	    !selectedPartnerCompatibilityError &&
	    (Boolean(myRegistration) || registrationsOpen) &&
	    (!String(couponCode || "").trim() || couponValidationStatus === "valid") &&
	    (registrationCategories.length === 0 ||
	      Boolean(selectedRegistrationCategoryKey)) &&
	    (!isFixedPairChallenge || teammateCpfDigits.length >= 8) &&
	    (!isDrawPairChallenge || Boolean(preferredSideChoice));
  const stepNumber =
    checkoutStep === "data" ? 1 : checkoutStep === "redirecting" ? 2 : 3;
  const currentCheckoutUrl =
    checkoutUrl ||
    resolveCheckoutUrl({
      invoiceUrl: myRegistration?.invoiceUrl,
      paymentLink: myRegistration?.paymentLink,
    });
  const shouldShowShirtCard =
    configuredShirtSizes.length > 0 && hasSelectedFixedPairPartner;
  const shirtSizeSelection =
    shouldShowShirtCard ? (
      <div className="event-register-v2-shirt-card">
        <h3 className="info-label">
          Tamanho da camisa dos jogadores
          <InfoTooltip content={info.shirt} />
        </h3>
        {shirtAvailabilityMessage && (
          <p className="event-register-v2-shirt-note">
            {shirtAvailabilityMessage}
            <InfoTooltip content={info.shirtUnavailable} />
          </p>
        )}
        {shouldShowShirtSizeSelection && (
          <div className="event-register-v2-shirt-grid">
            <label className="field">
              <span className="info-label">
                {playerShirtSizeLabel}
                <InfoTooltip content={info.shirt} />
              </span>
              <select
                value={playerShirtSize}
                onChange={(inputEvent) => {
                  setPlayerShirtSize(inputEvent.target.value);
                  setShirtSizeError("");
                }}
                disabled={myRegistrationLoading}
                aria-invalid={Boolean(shirtSizeError && !playerShirtSize)}
              >
                <option value="">Selecione</option>
                {availableShirtSizes.map((size) => (
                  <option key={`player-shirt-${size}`} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>

            {isFixedPairChallenge && (
              <label className="field">
                <span className="info-label">
                  {partnerShirtSizeLabel}
                  <InfoTooltip content={info.shirt} />
                </span>
                <select
                  value={partnerShirtSize}
                  onChange={(inputEvent) => {
                    setPartnerShirtSize(inputEvent.target.value);
                    setShirtSizeError("");
                  }}
                  disabled={myRegistrationLoading}
                  aria-invalid={Boolean(shirtSizeError && !partnerShirtSize)}
                >
                  <option value="">Selecione</option>
                  {availableShirtSizes.map((size) => (
                    <option key={`partner-shirt-${size}`} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
        {shirtSizeError && (
          <p className="event-register-v2-shirt-error">{shirtSizeError}</p>
        )}
      </div>
    ) : null;

  return (
    <AppLayout>
      {toast && (
        <Toast
          message={toast.message}
          variant={toast.variant}
          onClose={() => setToast(null)}
        />
      )}
      <article className="event-register-v2-shell">
        <div className="event-register-v2-head">
          <h1>{registrationPageTitle}</h1>
          <div className="event-register-v2-steps">
            <span className={stepNumber === 1 ? "active" : ""}>
              {siteTexts.eventRegistration.steps.data}
            </span>
            <span className={stepNumber === 2 ? "active" : ""}>
              {siteTexts.eventRegistration.steps.externalPayment}
            </span>
            <span className={stepNumber === 3 ? "active" : ""}>
              {siteTexts.eventRegistration.steps.confirmation}
            </span>
          </div>
        </div>

        {!challengeLinkLoading && !hasLink && (
          <p className="status-message">
            {siteTexts.eventRegistration.linkedChallengeMissing}
          </p>
        )}

        {challengeLinkLoading && (
          <p className="status-message">
            <Spinner label={siteTexts.eventRegistration.loadingEventInfo} />
          </p>
        )}
        {!challengeLinkLoading &&
          hasLink &&
          !myRegistration &&
          !registrationsOpen && (
            <p className="status-message">
              {siteTexts.eventRegistration.registrationClosed}
            </p>
          )}
        {!challengeLinkLoading && hasLink && categoryMismatchMessage && (
          <p className="status-message">{categoryMismatchMessage}</p>
        )}

        {checkoutStep === "data" ? (
          <div className="event-register-v2-grid">
            <section className="event-v2-panel event-register-v2-form">
              {registrationCategories.length > 0 &&
                selectableRegistrationCategories.length === 0 && (
                  <p className="status-message">
                    Você já está inscrita em todas as categorias disponíveis para
                    este torneio.
                  </p>
                )}

              {registrationCategories.length > 0 &&
                selectableRegistrationCategories.length > 0 && (
                <label className="field">
                  <h2 className="info-label">
                    {siteTexts.eventRegistration.form.registrationCategory}
                    <InfoTooltip content={info.registrationCategory} />
                  </h2>
                  <select
                    value={selectedRegistrationCategoryKey}
                    onChange={(inputEvent) =>
                      setSelectedRegistrationCategoryKey(
                        inputEvent.target.value,
                      )
                    }
                    disabled={myRegistrationLoading}
                  >
                    {selectableRegistrationCategories.map((item) => {
                      const key = String(item.categoryKey || "").trim();
                      const remaining = Number(item.remainingSlots || 0);
                      const scheduledTime = item.scheduledAt
                        ? formatTimeLabel(item.scheduledAt)
                        : "";
                      const disallowedByProfile =
                        Boolean(eligibleRegistrationCategoryKeys) &&
                        !eligibleRegistrationCategoryKeys.has(key);
                      return (
                        <option
                          key={`registration-category-${item.categoryKey}`}
                          value={item.categoryKey}
                          disabled={disallowedByProfile}
                        >
                          {`${item.categoryName}${
                            scheduledTime && scheduledTime !== "-"
                              ? ` - ${scheduledTime}`
                              : ""
                          } (${remaining} ${
                            siteTexts.eventRegistration.form.remainingSlots
                          }${
                            remaining <= 0
                              ? ` · ${siteTexts.eventRegistration.status.waitlistLabel}`
                              : ""
                          })${
                            disallowedByProfile
                              ? ` · ${siteTexts.eventRegistration.form.profileIncompatible}`
                              : ""
                          }`}
                        </option>
                      );
                    })}
                  </select>
                </label>
              )}

              {isSuper8Challenge && (
                <div className="event-register-v2-individual-note">
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    person_check
                  </span>
                  <p>{siteTexts.eventRegistration.form.super8Notice}</p>
                </div>
              )}

              {isDrawPairChallenge &&
                (registrationCategories.length === 0 ||
                  selectableRegistrationCategories.length > 0) && (
                  <div className="event-register-v2-partner-card">
                    <div className="event-register-v2-panel-title-row">
                      <h3>
                        {
                          siteTexts.eventRegistration.form.drawPairSide
                            .noticeTitle
                        }
                      </h3>
                    </div>
                    <p>
                      {siteTexts.eventRegistration.form.drawPairSide.notice}
                    </p>
                    <label className="field">
                      <span>
                        {
                          siteTexts.eventRegistration.form.drawPairSide
                            .question
                        }
                      </span>
                      <div className="event-register-v2-payment-methods-grid">
                        {(
                          [
                            {
                              id: "direita",
                              icon: "sports_tennis",
                              label: siteTexts.challengeEngine.labels.right,
                            },
                            {
                              id: "esquerda",
                              icon: "sports_tennis",
                              label: siteTexts.challengeEngine.labels.left,
                            },
                            {
                              id: "ambos",
                              icon: "sync_alt",
                              label:
                                siteTexts.eventRegistration.form.drawPairSide
                                  .both,
                            },
                          ] as const
                        ).map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            className={`event-register-v2-payment-method${
                              preferredSideChoice === option.id
                                ? " is-selected"
                                : ""
                            }`}
                            onClick={() => setPreferredSideChoice(option.id)}
                            disabled={myRegistrationLoading}
                          >
                            <span
                              className="material-symbols-outlined"
                              aria-hidden="true"
                            >
                              {option.icon}
                            </span>
                            <strong>{option.label}</strong>
                          </button>
                        ))}
                      </div>
                    </label>
                  </div>
                )}

              {availabilityRestrictionSelection}

              {isFixedPairChallenge &&
                (registrationCategories.length === 0 ||
                  selectableRegistrationCategories.length > 0) && (
	                <>
	                  <div className="event-register-v2-partner-card">
	                    <div className="event-register-v2-panel-title-row">
                      <h3 className="info-label">
                        {siteTexts.eventRegistration.form.fixedPair.title}
                        <InfoTooltip content={info.fixedPair} />
                      </h3>
	                    </div>
	                    {teammateCpfDigits.length >= 8 ? (
	                      <>
	                        <div className="event-register-v2-selected-partner">
	                          <span
	                            className="event-register-v2-partner-avatar"
	                            aria-hidden="true"
	                          >
	                            {initialsFromName(teammateNameDraft || "")}
	                          </span>
	                          <div className="event-register-v2-partner-info">
	                            <strong>
	                              {teammateNameDraft ||
	                                siteTexts.eventRegistration.summary.selectPair}
	                            </strong>
	                            <small>{formatCpf(teammateCpfDigits)}</small>
	                          </div>
	                          <Button
	                            type="button"
	                            variant="pill"
	                            onClick={() => void clearSelectedPair()}
	                            disabled={myRegistrationLoading}
	                          >
	                            Remover dupla
	                          </Button>
	                        </div>
	                        {shirtSizeSelection}
	                      </>
	                    ) : (
	                      <>
	                        <div className="field">
	                          <span className="info-label">
	                            {
	                              siteTexts.eventRegistration.form.fixedPair
	                                .searchLabel
	                            }
                              <InfoTooltip content={info.fixedPair} />
	                          </span>
	                          <SearchInput
	                            value={partnerSearch}
	                            onChange={(inputEvent) =>
	                              setPartnerSearch(inputEvent.target.value)
	                            }
	                            onClear={() => setPartnerSearch("")}
                            placeholder={
                              siteTexts.eventRegistration.form.fixedPair
                                .searchPlaceholder
                            }
                            disabled={myRegistrationLoading}
                          />
	                        </div>
	                        {partnersLoading ? (
	                          <p className="status-message">
	                            <Spinner
	                              label={
	                                siteTexts.eventRegistration.loadingPartners
	                              }
	                            />
	                          </p>
	                        ) : availablePartnersWithEligibility.length > 0 ? (
	                          <div className="event-register-v2-partner-results">
	                            {availablePartnersWithEligibility
	                              .slice(0, 8)
	                              .map((partner) => {
	                                const labelSecondary =
	                                  getPartnerCategoryStatus(partner);
	                                const canSelectPartner =
	                                  isPartnerSelectionAllowed(partner);
	                                const unavailableLabel =
	                                  partner.reason === "already_registered"
	                                    ? siteTexts.eventRegistration.buttons
	                                        .unavailablePartner
                                    : siteTexts.eventRegistration
                                        .partnerCompatibility.incompatible;
	                                return (
	                                  <div
	                                    key={`partner-${partner.id}`}
	                                    className="event-register-v2-partner-item"
	                                  >
	                                    <span
	                                      className="event-register-v2-partner-avatar"
	                                      aria-hidden="true"
	                                    >
	                                      {initialsFromName(partner.name || "")}
	                                    </span>
	                                    <div className="event-register-v2-partner-info">
	                                      <strong>{partner.name}</strong>
	                                      <small>{labelSecondary}</small>
	                                    </div>
	                                    {canSelectPartner ? (
	                                      <Button
	                                        type="button"
	                                        variant="primary"
	                                        onClick={() => {
	                                          setPartnerShirtSize("");
	                                          setShirtSizeError("");
	                                          setTeammateCpfDraft(
	                                            formatCpf(partner.cpf || ""),
	                                          );
	                                          setTeammateNameDraft(
	                                            partner.name || "",
	                                          );
	                                        }}
	                                      >
	                                        {
	                                          siteTexts.eventRegistration.buttons
	                                            .selectPartner
	                                        }
	                                      </Button>
	                                    ) : (
	                                      <span
	                                        className="event-register-v2-partner-badge"
	                                        aria-disabled="true"
	                                      >
	                                        {unavailableLabel}
	                                      </span>
	                                    )}
	                                  </div>
	                                );
	                              })}
	                          </div>
	                        ) : (
	                          <p className="status-message">
	                            {siteTexts.eventRegistration.emptyPartners}
	                          </p>
	                        )}
	                      </>
	                    )}
	                  </div>
	                </>
	              )}

            </section>

            <section className="event-v2-panel event-register-v2-summary">
              <h2>{siteTexts.eventRegistration.summary.title}</h2>
              <div className="event-register-v2-summary-card">
                <div className="event-register-v2-summary-content">
                  <p>
                    <span>{siteTexts.eventRegistration.summary.date}</span>
                    <strong>{summaryDateLabel}</strong>
                  </p>
                  <p>
                    <span>{siteTexts.eventRegistration.summary.location}</span>
                    <strong>{summaryLocation}</strong>
                  </p>
                  <p>
                    <span className="info-label">
                      {siteTexts.eventRegistration.summary.type}
                      <InfoTooltip
                        content={
                          isFixedPairChallenge ? info.fixedPair : info.tournamentFormat
                        }
                      />
                    </span>
                    <strong>
                      {isFixedPairChallenge
                        ? siteTexts.eventRegistration.summary.fixedPair
                        : siteTexts.eventRegistration.summary.individual}
                    </strong>
                  </p>
                  {isSuper8Challenge && (
                    <>
                      <p>
                        <span>{siteTexts.eventRegistration.summary.format}</span>
                        <strong>
                          {siteTexts.eventRegistration.summary.super8Format}
                        </strong>
                      </p>
                      <p>
                        <span>{siteTexts.eventRegistration.summary.rule}</span>
                        <strong>
                          {siteTexts.eventRegistration.summary.super8Rule}
                        </strong>
                      </p>
                    </>
                  )}
                  <p>
                    <span className="info-label">
                      {siteTexts.eventRegistration.summary.category}
                      <InfoTooltip content={info.registrationCategory} />
                    </span>
                    <strong>{challengeCategoryLabel}</strong>
                  </p>
                  {isFixedPairChallenge && (
                    <p>
                      <span>{siteTexts.eventRegistration.summary.players}</span>
                      <strong>
                        {payerName || fullName}
                        <br />
                        {teammateNameDraft ||
                          siteTexts.eventRegistration.summary.selectPair}
                      </strong>
                    </p>
                  )}
                  {isDrawPairChallenge && (
                    <p>
                      <span>
                        {
                          siteTexts.eventRegistration.form.drawPairSide
                            .summaryLabel
                        }
                      </span>
                      <strong>
                        {formatPreferredSide(preferredSideChoice)}
                      </strong>
                    </p>
                  )}
                  {shouldShowShirtSizeSelection && (
                    <p>
                      <span className="info-label">
                        Camisetas
                        <InfoTooltip
                          content={
                            challengeLink?.shirtUnavailableReason
                              ? info.shirtUnavailable
                              : info.shirt
                          }
                        />
                      </span>
                      <strong>
                        {playerShirtSize || "Selecione"}
                        {isFixedPairChallenge
                          ? ` / ${partnerShirtSize || "Selecione"}`
                          : ""}
                      </strong>
                    </p>
                  )}
                  {selectedAvailabilityRestrictionPeriods.length > 0 && (
                    <p>
                      <span>Restrição</span>
                      <strong>{selectedAvailabilityRestrictionLabels}</strong>
                    </p>
                  )}
                  <p>
                    <span className="info-label">
                      {siteTexts.eventRegistration.summary.valuePerPlayer}
                      <InfoTooltip content={info.valuePerPlayer} />
                    </span>
                    <strong>{amountPerPlayerLabel}</strong>
                  </p>
                  <div className="event-register-v2-payment-methods">
                    <span className="event-register-v2-payment-methods-title info-label">
                      {siteTexts.eventRegistration.paymentMethods.title}
                      <InfoTooltip content={info.externalPayment} />
                    </span>
                    <div className="event-register-v2-payment-methods-grid">
                      {(
                        [
                          {
                            id: "pix",
                            icon: "qr_code_2",
                            label:
                              siteTexts.eventRegistration.paymentMethods
                                .pixLabel,
                          },
                          {
                            id: "credit_card",
                            icon: "credit_score",
                            label:
                              siteTexts.eventRegistration.paymentMethods
                                .creditLabel,
                          },
                        ] as const
                      ).map((method) => (
                        <button
                          key={method.id}
                          type="button"
                          className={`event-register-v2-payment-method${
                            selectedPaymentMethod === method.id
                              ? " is-selected"
                              : ""
                          }`}
                          onClick={() =>
                            setSelectedPaymentMethod(
                              method.id as PaymentMethodChoice,
                            )
                          }
                        >
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            {method.icon}
                          </span>
                          <strong>{method.label}</strong>
                        </button>
                      ))}
                    </div>
                    <small className="event-register-v2-payment-methods-note">
                      {siteTexts.eventRegistration.paymentMethods.note}
                    </small>
                  </div>
                  <div className="event-register-v2-coupon">
                    <label>
                      <span className="info-label">
                        {siteTexts.eventRegistration.discountCoupon.label}
                        <InfoTooltip content={info.discountCoupon} />
                      </span>
                      <div className="event-register-v2-coupon-row">
                        <input
                          value={couponCode}
                          onChange={(event) =>
                            setCouponCode(event.target.value.toUpperCase())
                          }
                          placeholder={
                            siteTexts.eventRegistration.discountCoupon
                              .placeholder
                          }
                        />
                        <Button
                          type="button"
                          variant="primary"
                          className="event-register-v2-coupon-apply"
                          onClick={onApplyCoupon}
                          disabled={couponApplying}
                        >
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            sell
                          </span>
                          {couponApplying
                            ? siteTexts.eventRegistration.discountCoupon
                                .applying
                            : siteTexts.eventRegistration.discountCoupon.apply}
                        </Button>
                      </div>
                    </label>
                    {couponPreview?.couponCode && discountAmountCents > 0 && (
                      <small>
                        {
                          siteTexts.eventRegistration.discountCoupon
                            .successPrefix
                        }{" "}
                        <strong>{couponPreview.couponCode}</strong>
                      </small>
                    )}
                  </div>
                  {!shouldShowShirtSizeInPairCard && shirtSizeSelection}
                  <p>
                    <span className="info-label">
                      {siteTexts.eventRegistration.summary.subtotal}
                      <InfoTooltip content={info.subtotal} />
                    </span>
                    <strong>{baseTotalAmountLabel}</strong>
                  </p>
                  {selectedAvailabilityRestrictionPeriods.length > 0 &&
                    availabilityRestrictionAmountCents > 0 && (
                      <p>
                        <span>Restrição</span>
                        <strong>
                          {formatCurrency(availabilityRestrictionAmountCents)}
                        </strong>
                      </p>
                    )}
                  {discountAmountCents > 0 && (
                    <p>
                      <span>
                        {siteTexts.eventRegistration.summary.discount}
                      </span>
                      <strong>-{discountAmountLabel}</strong>
                    </p>
                  )}
                  <p>
                    <span className="info-label">
                      {siteTexts.eventRegistration.summary.fee}
                      <InfoTooltip content={info.methodFee} />
                    </span>
                    <strong>{paymentSurchargeLabel}</strong>
                  </p>
                  <p className="event-register-v2-total">
                    <span className="info-label">
                      {siteTexts.eventRegistration.summary.total}
                      <InfoTooltip content={info.registrationTotal} />
                    </span>
                    <strong>{totalAmountLabel}</strong>
                  </p>
                </div>
              </div>
              <div className="event-register-v2-actions">
                {!hasLink ? (
                  <Button variant="pill" type="button" disabled>
                    {siteTexts.eventRegistration.buttons.unavailable}
                  </Button>
                ) : !token ? (
                  <Button
                    variant="primary"
                    href={loginHref}
                    className="event-v2-cta-btn"
                  >
                    {siteTexts.eventDetails.loginToRegister}
                  </Button>
                ) : myRegistration?.status === "confirmed" &&
                  myRegistration?.paymentStatus === "confirmed" ? (
                  <Button variant="pill" type="button" disabled>
                    {siteTexts.eventRegistration.buttons.confirmed}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    type="button"
                    onClick={onSubmitAndPay}
                    disabled={
                      challengeLinkLoading ||
                      myRegistrationLoading ||
                      !canSubmitAndPay
                    }
                    className="event-v2-cta-btn"
                  >
                    {isSuper8Challenge
                      ? siteTexts.eventRegistration.buttons
                          .confirmMyRegistration
                      : siteTexts.eventRegistration.buttons.continueToPayment}
                  </Button>
                )}
              </div>
            </section>
          </div>
        ) : (
          <section className="event-v2-panel event-register-v2-checkout-state">
            {checkoutStep === "redirecting" ? (
              <div className="event-register-v2-checkout-card is-redirecting">
                <div
                  className="event-register-v2-checkout-icon"
                  aria-hidden="true"
                >
                  <span className="material-symbols-outlined">sync</span>
                </div>
                <h2>{siteTexts.eventRegistration.checkout.redirectingTitle}</h2>
                <p>{siteTexts.eventRegistration.checkout.redirectingText}</p>
                <div
                  className="event-register-v2-checkout-dots"
                  aria-hidden="true"
                >
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            ) : checkoutStep === "confirmed" ? (
              <div className="event-register-v2-checkout-card is-confirmed">
                <div
                  className="event-register-v2-checkout-icon"
                  aria-hidden="true"
                >
                  <span className="material-symbols-outlined">
                    check_circle
                  </span>
                </div>
                <h2>{siteTexts.eventRegistration.checkout.confirmedTitle}</h2>
                <p>{siteTexts.eventRegistration.checkout.confirmedText}</p>
                <div className="event-register-v2-checkout-actions">
                  <Button variant="primary" href="#/perfil">
                    {siteTexts.eventRegistration.buttons.viewMyRegistrations}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="event-register-v2-checkout-card">
                <div
                  className="event-register-v2-checkout-icon"
                  aria-hidden="true"
                >
                  <span className="material-symbols-outlined">schedule</span>
                </div>
                <h2>{siteTexts.eventRegistration.checkout.pendingTitle}</h2>
                <p>{siteTexts.eventRegistration.checkout.pendingText}</p>
                <div className="event-register-v2-checkout-actions">
                  {currentCheckoutUrl ? (
                    <Button
                      variant="primary"
                      type="button"
                      onClick={() => {
                        const opened =
                          openCheckoutInNewPage(currentCheckoutUrl);
                        if (!opened) {
                          setToast({
                            message:
                              siteTexts.eventRegistration.toasts.openPopupError,
                            variant: "error",
                          });
                          return;
                        }
                        setToast({
                          message:
                            siteTexts.eventRegistration.toasts
                              .openPaymentSuccess,
                          variant: "success",
                        });
                      }}
                    >
                      {siteTexts.eventRegistration.buttons.openPaymentAgain}
                    </Button>
                  ) : null}
                  <Button variant="pill" href="#/perfil">
                    {siteTexts.eventRegistration.buttons.viewMyRegistrations}
                  </Button>
                </div>
              </div>
            )}
          </section>
        )}

        {checkoutStep === "data" && (
          <div className="event-register-v2-status">
            {hasLink && !challengeLinkLoading && myRegistrationLoading && (
              <p className="status-message">
                <Spinner
                  label={siteTexts.eventRegistration.loadingMyRegistration}
                />
              </p>
            )}
            {!challengeLinkLoading && myRegistration?.status === "canceled" && (
              <p className="status-message">
                {siteTexts.eventRegistration.status.canceledPrefix}
                {myRegistration.adminNote
                  ? `: ${myRegistration.adminNote}`
                  : "."}
              </p>
            )}
            {!challengeLinkLoading &&
              myRegistration?.status === "confirmed" &&
              myRegistration.paymentRequired &&
              myRegistration.paymentStatus !== "confirmed" && (
                <p className="status-message">
                  {siteTexts.eventRegistration.status.pendingPayment}
                </p>
              )}
            {!challengeLinkLoading &&
              myRegistration?.participantMode === "dupla_fixa" &&
              myRegistration?.teammateCpf && (
                <p className="status-message">
                  {siteTexts.eventRegistration.status.fixedPairPrefix}{" "}
                  {myRegistration.teammateName
                    ? `${myRegistration.teammateName} · `
                    : ""}
                  {formatCpf(myRegistration.teammateCpf)}
                </p>
              )}
            {!challengeLinkLoading && myRegistration?.status === "waitlist" && (
              <p className="status-message">
                {siteTexts.eventRegistration.status.waitlist}
              </p>
            )}
            {!challengeLinkLoading &&
              (myRegistration?.invoiceUrl || myRegistration?.paymentLink) &&
              myRegistration?.paymentStatus !== "confirmed" && (
                <p className="status-message">
                  {siteTexts.eventRegistration.status.paymentLinkPrefix}{" "}
                  <a
                    href={
                      myRegistration.invoiceUrl ||
                      myRegistration.paymentLink ||
                      "#"
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    {myRegistration.invoiceUrl || myRegistration.paymentLink}
                  </a>
                </p>
              )}
          </div>
        )}
      </article>
    </AppLayout>
  );
}
